import { GaugeIcon, MinusIcon } from '@phosphor-icons/react'

import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { TrendingDown } from '@assets/svgs/TrendingDown'
import { TrendingUp } from '@assets/svgs/TrendingUp'
import { getAccountIcon } from '@pages/ConsolidatedEntry/components/ConsolidatedGrid/helpers/getAccountIcon'
import { findAccountConfig } from '@pages/ConsolidatedEntry/constants/accountsConfig'
import { getRealizedDeviationData } from './getRealizedDeviationData'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'
import { PreviewsStatus, type PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'

import type { AccountIconKey } from '@pages/ConsolidatedEntry/constants/accountsConfig'

import type { ColumnDefProps, GroupedHeaderCellProps } from '@components/shared/Table/types'
import type { ConsolidatedFinancialSummaryRowProps, WeekStatusCode } from '@services/api/previewManagement/types'

import * as S from '../styles'

// Status agregados da semana → status da prévia de mesma cor na tabela de prévias (BudgetPreviewsStatus)
const WEEK_STATUS_TO_PREVIEW_STATUS: Record<WeekStatusCode, PreviewsStatusProps> = {
  not_started: PreviewsStatus.NOT_STARTED,
  in_progress: PreviewsStatus.IN_PROGRESS,
  pending_approval: PreviewsStatus.GO_VALIDATION,
  completed: PreviewsStatus.COMPLETED,
  auto_send: PreviewsStatus.RELEASED_SEND
}

const CURRENT_WEEK_HEADER_COLOR = '#B0AFFAD9'

type DeviationDirection = 'up' | 'down' | 'neutral'

interface ParsedSummaryRow {
  label: string
  hasIcon: boolean
  budget: number
  budgetPercentage: number | null
  hideWeeklyPercentage: boolean
  hideDeviationPercentage: boolean
  hideTrendArrow: boolean
  weekValues: number[]
  weekPercentages: (number | null)[]
  weekTrendDirections: DeviationDirection[]
  weekTrendColors: string[]
  realizado: number
  realizedPercentage: number | null
  realizedDeviationAmount: number
  realizedDeviationPercentage: number | null
  realizedDeviationDirection: DeviationDirection
  deviationAmount: number
  deviationPercentage: number | null
  deviationDirection: DeviationDirection
  isPercentageRow?: boolean
  isTotalRow?: boolean
}

const TREND_CONFIG: Record<DeviationDirection, { icon: React.ReactNode }> = {
  up: { icon: <TrendingUp size="small" /> },
  down: { icon: <TrendingDown size="small" /> },
  neutral: { icon: <MinusIcon size={12} weight="bold" /> }
}

// Faturamento e Bonificação são receita: subir é bom (verde). As demais são despesa: subir é ruim (vermelho).
const REVENUE_ACCOUNT_KEYS: AccountIconKey[] = ['invoicing', 'bonus']
const EXPENSE_ACCOUNT_KEYS: AccountIconKey[] = ['consumption', 'people', 'depreciation', 'otherExpenses']

const getDirection = (value: number): DeviationDirection => (value > 0 ? 'up' : value < 0 ? 'down' : 'neutral')

const getWeekTrendColor = (accountKey: AccountIconKey | undefined, direction: DeviationDirection): string => {
  if (direction === 'neutral' || !accountKey) return emotionTheme.colors.primary

  if (REVENUE_ACCOUNT_KEYS.includes(accountKey)) {
    return direction === 'up' ? emotionTheme.colors.green.color : emotionTheme.colors.red.color
  }

  if (EXPENSE_ACCOUNT_KEYS.includes(accountKey)) {
    return direction === 'up' ? emotionTheme.colors.red.color : emotionTheme.colors.green.color
  }

  return emotionTheme.colors.primary
}

const toFinitePercentage = (value: number): number | null => (Number.isFinite(value) ? value : null)

const formatPercentage = (value: number) => `${value.toFixed(2)}%`.replace('.', ',')

const formatValue = (value: number, isPercentageRow?: boolean) =>
  isPercentageRow ? formatPercentage(value) : currencyMask(value)

const parseRow = (row: ConsolidatedFinancialSummaryRowProps): ParsedSummaryRow => {
  const isTotalRow = row.accountId === 0
  const isPercentageRow = row.account.toLowerCase().includes('margin')
  const accountKey = findAccountConfig(row.account)?.key
  const isInvoicing = accountKey === 'invoicing'
  // % escondido para totais, GM e Faturamento (RF01); a seta continua aparecendo — só o GM não tem seta,
  // porque o próprio valor da linha já é o percentual.
  const hideWeeklyPercentage = isTotalRow || isPercentageRow || isInvoicing
  const hideDeviationPercentage = isPercentageRow || isInvoicing
  const hideTrendArrow = isPercentageRow
  const realizedDeviation = getRealizedDeviationData(row.deviationVsRealizedSap, row.deviationVsRealizedSapPercentage)

  // Semana atual vs semana anterior; a Semana 1 compara com o Budget, por não haver "semana 0".
  const weekTrendDirections = row.weeks.map((week, index) => {
    const previousValue = index === 0 ? row.budget : row.weeks[index - 1].actualPreview
    return getDirection(week.actualPreview - previousValue)
  })

  return {
    label: row.account,
    hasIcon: !isTotalRow,
    budget: row.budget,
    budgetPercentage: toFinitePercentage(row.budgetPercentage ?? NaN),
    hideWeeklyPercentage,
    hideDeviationPercentage,
    hideTrendArrow,
    weekValues: row.weeks.map((week) => week.actualPreview),
    weekPercentages: row.weeks.map((week) => toFinitePercentage(week.actualPreviewPercentage ?? NaN)),
    weekTrendDirections,
    weekTrendColors: weekTrendDirections.map((direction) => getWeekTrendColor(accountKey, direction)),
    realizado: row.realizedAmount,
    realizedPercentage: toFinitePercentage(row.realizedPercentage ?? NaN),
    realizedDeviationAmount: realizedDeviation.amount,
    realizedDeviationPercentage: toFinitePercentage(realizedDeviation.percentage),
    realizedDeviationDirection: realizedDeviation.direction,
    deviationAmount: row.deviationVsBudget,
    deviationPercentage: toFinitePercentage(row.deviationVsBudgetPercentage),
    deviationDirection: getDirection(row.deviationVsBudget),
    isPercentageRow,
    isTotalRow
  }
}

const renderWeekCell = (
  value: number,
  percentage: number | null,
  direction: DeviationDirection,
  isPercentageRow: boolean | undefined,
  hidePercentage: boolean | undefined,
  hideArrow: boolean | undefined,
  trendColor: string,
  inline: boolean | undefined
) => (
  <S.CellWrapper inline={inline}>
    <S.CellValue>{formatValue(value, isPercentageRow)}</S.CellValue>
    {(!hidePercentage || !hideArrow) && (
      <S.CellTrend>
        {!hidePercentage && (percentage === null ? '- %' : formatPercentage(percentage))}
        {!hideArrow && <S.IconWrapper color={trendColor}>{TREND_CONFIG[direction].icon}</S.IconWrapper>}
      </S.CellTrend>
    )}
  </S.CellWrapper>
)

const formatDeviationValue = (value: number, isPercentageRow?: boolean) => {
  if (isPercentageRow) return `${value.toFixed(2).replace('.', ',')}%`

  return currencyMask(value)
}

const renderDeviationCell = (row: ParsedSummaryRow) => (
  <S.CellWrapper inline={row.isTotalRow}>
    <S.CellValue>{formatDeviationValue(row.deviationAmount, row.isPercentageRow)}</S.CellValue>
    {(!row.hideDeviationPercentage || !row.hideTrendArrow) && (
      <S.CellTrend>
        {!row.hideDeviationPercentage &&
          (row.deviationPercentage === null ? '- %' : formatPercentage(row.deviationPercentage))}
        {!row.hideTrendArrow && (
          <S.IconWrapper color={emotionTheme.colors.primary}>{TREND_CONFIG[row.deviationDirection].icon}</S.IconWrapper>
        )}
      </S.CellTrend>
    )}
  </S.CellWrapper>
)

const buildRow = (columns: ColumnDefProps[], row: ParsedSummaryRow, weekCount: number): ColumnDefProps[] => {
  const deviationColumnIndex = 2 + weekCount + 2
  const account = row.hasIcon ? getAccountIcon(row.label) : undefined

  return [
    {
      ...columns[0],
      value: account ? (
        <S.RowLabel>
          <S.AccountIconWrapper color={account.color}>{account.icon}</S.AccountIconWrapper>
          {row.label}
        </S.RowLabel>
      ) : row.isPercentageRow ? (
        <S.GmLabel>
          <GaugeIcon size={18} />
          {row.label}
        </S.GmLabel>
      ) : (
        row.label
      ),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    },
    {
      ...columns[1],
      value: (
        <S.CellWrapper>
          <S.CellValue>{formatValue(row.budget, row.isPercentageRow)}</S.CellValue>
          {!row.hideWeeklyPercentage && (
            <S.CellTrend>{row.budgetPercentage === null ? '- %' : formatPercentage(row.budgetPercentage)}</S.CellTrend>
          )}
        </S.CellWrapper>
      ),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    },
    ...row.weekValues.map((weekValue, index) => ({
      ...columns[2 + index],
      value: renderWeekCell(
        weekValue,
        row.weekPercentages[index],
        row.weekTrendDirections[index],
        row.isPercentageRow,
        row.hideWeeklyPercentage,
        row.hideTrendArrow,
        row.weekTrendColors[index],
        row.isTotalRow
      ),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    })),
    {
      ...columns[2 + weekCount],
      value: (
        <S.CellWrapper>
          <S.CellValue>{formatValue(row.realizado, row.isPercentageRow)}</S.CellValue>
          {!row.hideWeeklyPercentage && (
            <S.CellTrend>{row.realizedPercentage === null ? '- %' : formatPercentage(row.realizedPercentage)}</S.CellTrend>
          )}
        </S.CellWrapper>
      ),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    },
    {
      ...columns[3 + weekCount],
      value: renderDeviationCell(row),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    },
    {
      ...columns[deviationColumnIndex],
      value: renderDeviationCell({
        ...row,
        deviationAmount: row.realizedDeviationAmount,
        deviationPercentage: row.realizedDeviationPercentage,
        deviationDirection: row.realizedDeviationDirection
      }),
      fontWeight: row.isTotalRow ? 600 : undefined,
      withoutTooltip: true
    }
  ]
}

export const parseConsolidatedSummaryGrid = (rows: ConsolidatedFinancialSummaryRowProps[] = []) => {
  const weekNumbers = rows[0]?.weeks.map((week) => week.weekNumber) ?? []
  const currentWeekNumber = rows[0]?.weeks.find((week) => week.isCurrentPreview)?.weekNumber
  const weekByNumber = new Map(rows[0]?.weeks.map((week) => [week.weekNumber, week]) ?? [])

  const columns: ColumnDefProps[] = [
    {
      id: 'account',
      columnName: 'Conta',
      value: '',
      headerAlign: 'center',
      bodyAlign: 'left',
      minWidth: '12rem'
    },
    { id: 'budget', columnName: 'Budget', value: '', align: 'right', headerAlign: 'center', minWidth: '9rem' },
    ...weekNumbers.map((weekNumber) => ({
      id: `week-${weekNumber}`,
      columnName: (
        <S.WeekHeader>
          <span>{`Semana ${weekNumber}`}</span>
          {weekByNumber.get(weekNumber)?.weekStatusLabel && (
            <BudgetPreviewsStatus
              compact
              status={{
                code: WEEK_STATUS_TO_PREVIEW_STATUS[weekByNumber.get(weekNumber)?.weekStatus ?? 'not_started'],
                descriptionPtbr: weekByNumber.get(weekNumber)?.weekStatusLabel
              }}
            />
          )}
        </S.WeekHeader>
      ),
      value: '',
      align: 'right' as const,
      headerAlign: 'center' as const,
      minWidth: '9rem',
      columnColor: weekNumber === currentWeekNumber ? CURRENT_WEEK_HEADER_COLOR : undefined,
      bodyColumnColor: emotionTheme.colors.lightest
    })),
    { id: 'realizado', columnName: 'Realizado', value: '', align: 'right', headerAlign: 'center', minWidth: '9rem' },
    { id: 'deviation', columnName: 'Desvio (Sem. Atual vs BGT)', value: '', align: 'right', headerAlign: 'center', minWidth: '9rem' },
    {
      id: 'deviation-vs-realized',
      columnName: 'Desvio (Prévia vs Realizado)',
      value: '',
      align: 'right',
      headerAlign: 'center',
      minWidth: '9rem'
    }
  ]

  const groupedHeaderRow: GroupedHeaderCellProps[] = [
    { label: '', colSpan: 1 },
    { label: '', colSpan: 1 },
    { label: 'Evolução de Prévias', colSpan: weekNumbers.length, divider: true },
    { label: '', colSpan: 1 },
    { label: '', colSpan: 1 },
    { label: '', colSpan: 1 }
  ]

  const accountRows = rows.filter((row) => row.accountId !== 0)
  const totalRows = rows.filter((row) => row.accountId === 0)

  const data: ColumnDefProps[][] = accountRows.map((row) => buildRow(columns, parseRow(row), weekNumbers.length))
  const totalData: ColumnDefProps[][] = totalRows.map((row) => buildRow(columns, parseRow(row), weekNumbers.length))

  return { data, totalData, groupedHeaderRow }
}
